import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  BookOpenText,
  FilterX,
  Loader2,
  RefreshCw,
  Search,
  Shield,
} from "lucide-react";
import api from "../../api";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

type Severity = "ALL" | "INFO" | "WARN" | "CRITICAL";

interface AuditLogItem {
  id: string;
  createdAt: string;
  action: string;
  severity: Exclude<Severity, "ALL">;
  actorType: "human" | "system";
  actorUserId: string;
  actorEmail: string;
  actorName: string | null;
  targetType: string | null;
  targetId: string | null;
  eventId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  justification: string | null;
  metadata: unknown;
}

interface AuditLogResponse {
  items: AuditLogItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface AuditLogsPageProps {
  canView: boolean;
}

const severityClasses: Record<Exclude<Severity, "ALL">, string> = {
  INFO: "border-blue-200 bg-blue-50 text-blue-700",
  WARN: "border-amber-200 bg-amber-50 text-amber-700",
  CRITICAL: "border-red-200 bg-red-50 text-red-700",
};

const primaryButtonClass = "bg-[#1e3a5f] text-white hover:bg-[#0f2744] hover:text-white disabled:text-white";

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
}

function summarizeMetadata(metadata: unknown): string {
  if (!metadata || typeof metadata !== "object") return "-";
  const entries = Object.entries(metadata as Record<string, unknown>)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .slice(0, 6);
  if (entries.length === 0) return "-";
  return entries
    .map(([key, value]) => {
      if (typeof value === "object") return `${key}: ${Array.isArray(value) ? `[${value.length}]` : "{...}"}`;
      return `${key}: ${String(value).slice(0, 80)}`;
    })
    .join(" · ");
}

export function AuditLogsPage({ canView }: AuditLogsPageProps) {
  const [items, setItems] = useState<AuditLogItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [action, setAction] = useState("");
  const [actorEmail, setActorEmail] = useState("");
  const [severity, setSeverity] = useState<Severity>("ALL");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = { page, pageSize };
    if (from) params.from = from;
    if (to) params.to = to;
    if (action.trim()) params.action = action.trim();
    if (actorEmail.trim().length >= 3) params.actorEmail = actorEmail.trim();
    if (severity !== "ALL") params.severity = severity;
    if (q.trim().length >= 3) params.q = q.trim();
    return params;
  }, [action, actorEmail, from, page, pageSize, q, severity, to]);

  const loadLogs = useCallback(async () => {
    if (!canView) return;
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await api.get<AuditLogResponse>("/admin/audit-logs", {
        params: queryParams,
        headers: { "X-Passive-Request": "true" },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch {
      setItems([]);
      setError("No se pudo cargar la bitácora de auditoría.");
    } finally {
      setIsLoading(false);
    }
  }, [canView, queryParams]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const clearFilters = () => {
    setFrom("");
    setTo("");
    setAction("");
    setActorEmail("");
    setSeverity("ALL");
    setQ("");
    setPage(1);
  };

  if (!canView) {
    return (
      <Card className="border-red-100 bg-white">
        <CardContent className="p-12 text-center">
          <Shield className="mx-auto mb-4 h-12 w-12 text-red-300" />
          <h2 className="text-xl font-bold text-[#0f2744]">Acceso restringido</h2>
          <p className="mt-2 text-sm text-slate-600">No tienes permisos para consultar la Bitácora de Auditoría.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-[#0f2744]">Bitácora de Auditoría</h1>
            <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">Solo lectura</Badge>
          </div>
          <p className="text-sm text-slate-600">Consulta trazabilidad administrativa y operativa con metadata sanitizada.</p>
        </div>
        <Button onClick={loadLogs} disabled={isLoading} className={primaryButtonClass}>
          {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Actualizar
        </Button>
      </div>

      <Alert className="border-blue-200 bg-blue-50">
        <Shield className="h-4 w-4 text-blue-700" />
        <AlertDescription className="text-blue-900">
          Este módulo no muestra tokens, cookies, firmas, payloads crudos, CURP, teléfonos ni datos médicos.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="text-base text-[#0f2744]">Filtros</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
          <div className="space-y-2">
            <Label>Desde</Label>
            <Input type="date" value={from} onChange={(event) => { setFrom(event.target.value); setPage(1); }} />
          </div>
          <div className="space-y-2">
            <Label>Hasta</Label>
            <Input type="date" value={to} onChange={(event) => { setTo(event.target.value); setPage(1); }} />
          </div>
          <div className="space-y-2">
            <Label>Acción</Label>
            <Input value={action} onChange={(event) => { setAction(event.target.value); setPage(1); }} placeholder="LOGIN_SUCCESS" />
          </div>
          <div className="space-y-2">
            <Label>Correo actor</Label>
            <Input value={actorEmail} onChange={(event) => { setActorEmail(event.target.value); setPage(1); }} placeholder="admin@unach.mx" />
          </div>
          <div className="space-y-2">
            <Label>Severidad</Label>
            <Select value={severity} onValueChange={(value: Severity) => { setSeverity(value); setPage(1); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todas</SelectItem>
                <SelectItem value="INFO">Info</SelectItem>
                <SelectItem value="WARN">Warn</SelectItem>
                <SelectItem value="CRITICAL">Crítica</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Buscar</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input className="pl-9" value={q} onChange={(event) => { setQ(event.target.value); setPage(1); }} placeholder="mín. 3 caracteres" />
              </div>
              <Button variant="outline" onClick={clearFilters} title="Limpiar filtros">
                <FilterX className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-700" />
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      <Card className="overflow-hidden border-slate-200 bg-white shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base text-[#0f2744]">
            <BookOpenText className="h-4 w-4" />
            Eventos de auditoría
          </CardTitle>
          <span className="text-sm text-slate-500">{total} registros</span>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[62vh] max-w-full overflow-auto border-y border-slate-200 bg-white">
          <table className="w-full min-w-[1320px] border-separate border-spacing-0 text-sm">
            <thead>
              <tr className="bg-slate-50">
                <th className="sticky left-0 top-0 z-50 h-10 w-[190px] min-w-[190px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">Fecha</th>
                <th className="sticky left-[190px] top-0 z-50 h-10 w-[250px] min-w-[250px] border-b border-r border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[5px_0_10px_-8px_rgba(15,23,42,0.65),0_1px_0_rgba(15,23,42,0.08)]">Actor</th>
                <th className="sticky top-0 z-40 h-10 w-[220px] min-w-[220px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">Acción</th>
                <th className="sticky top-0 z-40 h-10 w-[170px] min-w-[170px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">Módulo / Severidad</th>
                <th className="sticky top-0 z-40 h-10 w-[130px] min-w-[130px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">IP</th>
                <th className="sticky top-0 z-40 h-10 w-[560px] min-w-[560px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#1e3a5f]" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-500">No se encontraron eventos de auditoría.</td>
                </tr>
              ) : (
                items.map((item) => {
                  const detailSummary = summarizeMetadata(item.metadata);
                  const detailTitle = item.justification
                    ? `${detailSummary}\nJustificación: ${item.justification}`
                    : detailSummary;

                  return (
                    <tr key={item.id} className="group hover:bg-blue-50/30">
                      <td className="sticky left-0 z-10 w-[190px] min-w-[190px] whitespace-nowrap border-b border-slate-100 bg-white px-3 py-3 text-sm text-slate-900 group-hover:bg-blue-50/80">{formatDate(item.createdAt)}</td>
                      <td className="sticky left-[190px] z-10 w-[250px] min-w-[250px] border-b border-r border-slate-100 bg-white px-3 py-3 shadow-[5px_0_10px_-8px_rgba(15,23,42,0.55)] group-hover:bg-blue-50/80">
                        <div className="font-medium text-[#0f2744]">{item.actorName || "Sin nombre"}</div>
                        <div className="break-all text-xs text-slate-500">
                          {item.actorType === "system" ? "Actor automático" : item.actorEmail}
                        </div>
                      </td>
                      <td className="w-[220px] min-w-[220px] border-b border-slate-100 px-3 py-3 font-mono text-xs leading-relaxed text-slate-800">{item.action}</td>
                      <td className="w-[170px] min-w-[170px] border-b border-slate-100 px-3 py-3">
                        <div className="flex flex-col gap-1.5">
                          <span className="text-sm text-slate-700">{item.targetType || "system"}</span>
                          <Badge variant="outline" className={`w-fit whitespace-nowrap ${severityClasses[item.severity]}`}>{item.severity}</Badge>
                        </div>
                      </td>
                      <td className="w-[130px] min-w-[130px] whitespace-nowrap border-b border-slate-100 px-3 py-3 text-sm text-slate-700">{item.ipAddress || "-"}</td>
                      <td className="w-[560px] min-w-[560px] border-b border-slate-100 px-3 py-3 text-xs text-slate-600" title={detailTitle}>
                        <div className="line-clamp-2 max-w-[520px] whitespace-normal break-words leading-relaxed">{detailSummary}</div>
                        {item.justification && <div className="mt-1 line-clamp-1 max-w-[520px] text-slate-500">Justificación: {item.justification}</div>}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-100 p-4 md:flex-row md:items-center md:justify-between">
            <span className="text-sm text-slate-600">
              Página {page} de {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(Number(value)); setPage(1); }}>
                <SelectTrigger className="w-[110px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" disabled={page <= 1 || isLoading} onClick={() => setPage((current) => Math.max(current - 1, 1))}>Anterior</Button>
              <Button variant="outline" disabled={page >= totalPages || isLoading} onClick={() => setPage((current) => current + 1)}>Siguiente</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
